import {
  type FunctionComponent,
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import { UseFormReturn, useForm } from 'react-hook-form';
import { useHistory } from 'react-router-dom';

import { StringIndexedObject, useToast } from '@kanda-libs/ks-design-library';
import { FinanceRate, Job, actions } from '@kanda-libs/ks-frontend-services';

import { useAppDispatch } from 'components/App';
import { URLS } from 'config';
import { extractErrorMessage, scrollToTop } from 'utils';
import { GroupedByAprAndDuration } from 'utils/FinanceRate-functions';

export type APRType = NonNullable<FinanceRate['apr_type']>;

export interface FingerprintBoolean {
  value: boolean;
  timestamp: number;
  ip: string;
}

export interface FinanceFormValues {
  apr: string;
  aprType: APRType;
  duration?: number;
  deferredDuration?: number;
  deposit?: number;
  depositPercent?: string;
  commission?: FingerprintBoolean;
  rate?: FinanceRate;
}

export interface FinanceFormContextType {
  allRates: GroupedByAprAndDuration;
  job: Job;
  form: UseFormReturn<FinanceFormValues>;
  onSubmit: (formValues: StringIndexedObject<FinanceFormValues>) => void;
  termRef: React.MutableRefObject<HTMLDivElement | null>;
  isSubmitting: boolean;
}

export const FinanceFormContext = createContext<FinanceFormContextType | null>(
  null,
);

export const useFinanceFormContext = () =>
  useContext(FinanceFormContext) as FinanceFormContextType;

export interface FinanceFormProviderProps {
  children: ReactNode;
  allRates: GroupedByAprAndDuration;
  job: Job;
}

const FinanceFormProvider: FunctionComponent<FinanceFormProviderProps> =
  function ({ children, allRates, job }) {
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const { showError } = useToast();
    const { push } = useHistory();
    const dispatch = useAppDispatch();

    const form = useForm<FinanceFormValues>({
      mode: 'onBlur',
      reValidateMode: 'onBlur',
    });

    const onSubmit = useCallback(
      (formValues: StringIndexedObject<FinanceFormValues>) => {
        const fv = formValues as unknown as FinanceFormValues;
        const id = job?.id;
        if (!id) {
          showError(
            'We cannot get access to your job information at this time - please refresh and try again. If this issue persists, please reach out to Ideal4Finance',
          );
          return;
        }

        const { rate, deposit } = fv;
        if (rate === undefined || deposit === undefined) {
          showError(
            'We cannot determine which finance rate you are applying for - please refresh and try again. If this issue persists, please reach out to Ideal4Finance',
          );
          return;
        }
        setIsSubmitting(true);
        dispatch(
          actions.applyJob({
            body: {
              checkout_option: 'apply_for_finance',
              finance_option: rate,
              deposit_value: { currency: 'GBP', amount: deposit },
            },
            params: { id },
            onError: (err) => {
              const message = extractErrorMessage(err);
              showError(message);
              setIsSubmitting(false);
            },
            onSuccess: () => {
              setIsSubmitting(false);
              push(URLS.applyForFinanceWelcome.replace(':id?', id));
              setTimeout(() => scrollToTop(), 10);
            },
          }),
        );
      },
      [dispatch, job, push, showError],
    );

    const termRef = useRef<HTMLDivElement | null>(null);

    // const filteredAllRates = useMemo(
    //   () =>
    //     Object.keys(allRates).reduce((final, apr) => {
    //       const durationRates = allRates[apr];
    //       const mapped = Object.keys(durationRates).reduce(
    //         (grouped, duration) => {
    //           const rates = durationRates[duration];
    //           return {
    //             ...grouped,
    //             [duration]: rates.map((r) =>
    //               r.provider !== 'allium'
    //                 ? r
    //                 : {
    //                     ...r,
    //                     max_deposit_pct: 2500,
    //                   },
    //             ),
    //           };
    //         },
    //         {},
    //       );
    //       return {
    //         ...final,
    //         [apr]: mapped,
    //       };
    //     }, {}),
    //   [allRates],
    // );

    const values = useMemo(
      () => ({
        allRates,
        job,
        termRef,
        form,
        onSubmit,
        isSubmitting,
      }),
      [allRates, job, termRef, form, onSubmit, isSubmitting],
    );

    return (
      <FinanceFormContext.Provider value={values}>
        {children}
      </FinanceFormContext.Provider>
    );
  };

export default FinanceFormProvider;
